import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { repositorioPanel } from '../../../data/repositories/RepositorioPanel';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { PanelPersonal, type ColorMarcador } from '../../../domain/entities/PanelPersonal';
import type { Sensor } from '../../../domain/entities/Sensor';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { useSesionStore } from '../../stores/sesionStore';

/** Espera antes de guardar automáticamente tras un cambio. */
const RETARDO_GUARDADO_MS = 1200;

export type EstadoGuardado = 'guardado' | 'guardando' | 'pendiente' | 'error';

/**
 * Estado del panel personal.
 *
 * Los cambios se guardan solos poco después de hacerlos, sin botón. Obligar a
 * pulsar «guardar» tras arrastrar un marcador es una forma segura de que la
 * gente pierda su trabajo.
 */
export function usePanel() {
  const perfil = useSesionStore((s) => s.perfil);
  const usuarioId = perfil?.id ?? '';

  const [panel, setPanel] = useState<PanelPersonal | null>(null);
  const [editando, setEditando] = useState(false);
  const [colorActivo, setColorActivo] = useState<ColorMarcador>('verde');
  const [guardado, setGuardado] = useState<EstadoGuardado>('guardado');
  const [subiendoImagen, setSubiendoImagen] = useState(false);

  const temporizadorGuardado = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<PanelPersonal | null>(null);
  panelRef.current = panel;

  const consultaPanel = useQuery({
    queryKey: ['panel', usuarioId],
    queryFn: () => repositorioPanel.obtener(usuarioId),
    enabled: usuarioId !== '',
  });

  useEffect(() => {
    if (consultaPanel.data) setPanel(consultaPanel.data);
  }, [consultaPanel.data]);

  // Los sensores se leen enteros porque el panel puede apuntar a cualquiera y
  // la lista completa se reutiliza en el selector.
  const consultaSensores = useQuery({
    queryKey: ['sensores', {}],
    queryFn: () => repositorioSensores.listarConValores({}),
  });

  const sensores = useMemo(() => consultaSensores.data ?? [], [consultaSensores.data]);

  const sensoresPorId = useMemo(() => {
    const mapa = new Map<number, Sensor>();
    for (const s of sensores) mapa.set(s.id, s);
    return mapa;
  }, [sensores]);

  const guardar = useCallback(async (siguiente: PanelPersonal) => {
    setGuardado('guardando');
    try {
      await repositorioPanel.guardar(siguiente);
      setGuardado('guardado');
    } catch (e) {
      registrarError('panel.guardar', e);
      setGuardado('error');
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudieron guardar los cambios');
    }
  }, []);

  /** Aplica un cambio y programa su guardado. */
  const cambiar = useCallback(
    (transformar: (actual: PanelPersonal) => PanelPersonal) => {
      setPanel((actual) => {
        if (!actual) return actual;

        const siguiente = transformar(actual);
        setGuardado('pendiente');

        if (temporizadorGuardado.current) clearTimeout(temporizadorGuardado.current);
        temporizadorGuardado.current = setTimeout(
          () => void guardar(siguiente),
          RETARDO_GUARDADO_MS,
        );

        return siguiente;
      });
    },
    [guardar],
  );

  // Un guardado pendiente al desmontar se perdería; se ejecuta de inmediato.
  useEffect(() => {
    return () => {
      if (temporizadorGuardado.current) clearTimeout(temporizadorGuardado.current);
    };
  }, []);

  /** Guarda ya, sin esperar al diferido. Devuelve si se pudo. */
  const guardarAhora = useCallback(async (): Promise<boolean> => {
    if (temporizadorGuardado.current) {
      clearTimeout(temporizadorGuardado.current);
      temporizadorGuardado.current = null;
    }

    const actual = panelRef.current;
    if (!actual) return false;

    setGuardado('guardando');
    try {
      await repositorioPanel.guardar(actual);
      setGuardado('guardado');
      return true;
    } catch (e) {
      registrarError('panel.guardar', e);
      setGuardado('error');
      return false;
    }
  }, []);

  const subirImagen = useCallback(
    async (archivo: File) => {
      if (!panel) return;

      setSubiendoImagen(true);
      try {
        const url = await repositorioPanel.subirImagen(usuarioId, archivo);
        cambiar((actual) => actual.conImagen(url));
        toast.success('Plano actualizado');
      } catch (e) {
        registrarError('panel.subirImagen', e);
        toast.error(e instanceof ErrorApp ? e.message : 'No se pudo subir la imagen');
      } finally {
        setSubiendoImagen(false);
      }
    },
    [panel, usuarioId, cambiar],
  );

  const refrescarValores = useCallback(() => {
    void consultaSensores.refetch();
  }, [consultaSensores]);

  useTiempoReal({ tabla: 'last_sensor_values', evento: '*' }, refrescarValores);
  useTiempoReal({ tabla: 'sensores_runtime', evento: 'UPDATE' }, refrescarValores);

  /** Datos ya resueltos de un sensor referenciado por el panel. */
  const sensorDe = useCallback(
    (sensorId: number | null) => (sensorId === null ? null : (sensoresPorId.get(sensorId) ?? null)),
    [sensoresPorId],
  );

  return {
    panel,
    sensores,
    sensorDe,
    cargando: consultaPanel.isLoading || consultaSensores.isLoading,
    error: consultaPanel.isError,

    editando,
    alternarEdicion: () => setEditando((v) => !v),
    colorActivo,
    setColorActivo,
    guardado,
    subiendoImagen,

    cambiar,
    guardarAhora,
    subirImagen,
  };
}
